<template>
  <BlokkliProvider
    v-if="term"
    v-slot="{ isEditing }"
    v-bind="term.blokkliProps"
    class="container"
  >
    <PageHeader :title="term.name" :lead="term.description" />

    <BlokkliField
      name="field_product_category_teasers"
      :list="term.paragraphTeasers"
      :list-class="isEditing ? 'grid grid-cols-5 gap-20 mb-40' : undefined"
      :edit-only="!isEditing"
    >
      <template #after="{ items }">
        <div
          class="grid grid-cols-2 lg:grid-cols-4 md:grid-cols-3 gap-15 lg:gap-30"
        >
          <SearchSidebar
            :facets="facets"
            :filters="filters"
            :sorting="filterSorting"
          />

          <ProductList
            :is-editing="isEditing"
            :teasers="items"
            :products="hitsVisible"
          />
        </div>
      </template>
    </BlokkliField>

    <LoadMore
      :per-page="perPage"
      :page="pageNumeric"
      :total="data?.total || 0"
    />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { TaxonomyTermProductCategoryFragment } from '#graphql-operations'
import type {
  ApiSearchProductResultItem,
  ApiSearchResponse,
} from '~/layers/search/types'

definePageMeta({
  name: 'product-category-list',
  path: '/en/products/:category',
  languageMapping: {
    en: '/en/products/:category',
    de: '/de/produkte/:category',
    fr: '/fr/produits/:category',
  },
  drupalRoute: true,
})

const language = useCurrentLanguage()
const page = useQueryString('page', '0')
const pageNumeric = computed(() => {
  const v = parseInt(page.value)
  if (Number.isInteger(v)) {
    return v
  }
  return 0
})
const { region } = useCountry()

const route = useRoute()

const { data: query } = await useAsyncGraphqlQuery(
  'routeProductCategory',
  {
    path: route.path,
  },
  {
    transform: function (data) {
      return data.data
    },
    graphqlCaching: {
      client: true,
    },
    deep: false,
  },
)
defineSchemaOrg(query)

const { entity: term } =
  await useDrupalRoute<TaxonomyTermProductCategoryFragment>(query.value)

setLanguageLinksFromRoute(query.value)

const productSearchQuery = computed(() => {
  return {
    ...route.query,
    mainCategory: term.value.tid,
    language: language.value,
    region: region.value,
  }
})

const { data } = await useFetch<ApiSearchResponse<ApiSearchProductResultItem>>(
  '/api/search/products',
  {
    query: productSearchQuery,
  },
)

const perPage = 31
const facets = computed(() => data.value?.facets || [])
const filters = computed(() => data.value?.filters || [])
const filterSorting = computed(() => data.value?.filterSorting || [])

const hitsVisible = computed(
  () => data.value?.hits.slice(0, (pageNumeric.value + 1) * perPage) || [],
)

const schemaOrg = () => {
  const json = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ItemList',
          name: term.value.name,
          description: term.value.description,
          url: 'https://www.exped.com' + route.path,
          numberOfItems: hitsVisible.value.length,
          itemListElement: data.value?.hits.map((hit, index) => {
            const hash = hit.variations.length
              ? hit.variations[0].rokka_hash
              : ''
            return {
              '@type': 'ListItem',
              position: index,
              item: {
                '@type': 'Product',
                name: hit.title,
                url: hit.url,
                image: hash
                  ? generateRokkaUrl(hash, hit.variations[0]?.sku)
                  : '',
              },
            }
          }),
        },
      ],
    },
    null,
    2,
  )

  return {
    type: 'application/ld+json',
    children: json,
  }
}

useHead({
  script: [schemaOrg()],
})

defineOptions({
  name: 'PageProductsCategory',
})

setBreadcrumbLinksFromRoute(query.value)
await renderPageDependencies()
</script>
